import { useState, useEffect } from "react"
import { Star, Users, Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { CategoryCombobox } from "@/components/ui/category-combobox"
import { usePromptStore } from "@/stores/prompt-store"
import { cn } from "@/lib/utils"

interface SavePromptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialContent?: string
  onSave?: (prompt: { title: string; content: string; isPinned: boolean }) => void
}

export function SavePromptDialog({
  open,
  onOpenChange,
  initialContent = "",
  onSave,
}: SavePromptDialogProps) {
  const [title, setTitle] = useState("")
  const [content, setContent] = useState(initialContent)
  const [isPinned, setIsPinned] = useState(false)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [visibility, setVisibility] = useState<'personal' | 'shared'>('personal')
  const [isSaving, setIsSaving] = useState(false)

  const { createPrompt, createCategory, categories, fetchCategories } = usePromptStore()

  // Fetch categories + reset form when dialog opens
  useEffect(() => {
    if (open) {
      setContent(initialContent)
      setTitle("")
      setIsPinned(false)
      setCategoryId(null)
      setVisibility('personal')
      void fetchCategories()
    }
  }, [open, initialContent, fetchCategories])

  const handleSave = async () => {
    if (!title.trim() || !content.trim() || isSaving) return

    setIsSaving(true)
    try {
      await createPrompt({
        title: title.trim(),
        content: content.trim(),
        isPinned,
        categoryId,
        visibility,
      })

      if (onSave) {
        onSave({ title: title.trim(), content: content.trim(), isPinned })
      }

      onOpenChange(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save to Prompt Library</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="prompt-title">Title</Label>
            <Input
              id="prompt-title"
              placeholder="e.g., Compliance Requirements Check"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt-content">Prompt</Label>
            <Textarea
              id="prompt-content"
              placeholder="Enter your prompt..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[100px]"
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1 space-y-2">
              <Label>Category</Label>
              <CategoryCombobox
                categories={categories}
                value={categoryId}
                onChange={setCategoryId}
                onCreateCategory={createCategory}
              />
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <div className="flex rounded-lg border border-border overflow-hidden h-9">
                <button
                  type="button"
                  className={cn(
                    "px-3 text-xs transition-colors",
                    visibility === 'personal'
                      ? "bg-foreground text-background font-medium"
                      : "hover:bg-accent text-muted-foreground"
                  )}
                  onClick={() => setVisibility('personal')}
                >
                  Personal
                </button>
                <button
                  type="button"
                  className={cn(
                    "px-3 text-xs transition-colors flex items-center gap-1",
                    visibility === 'shared'
                      ? "bg-foreground text-background font-medium"
                      : "hover:bg-accent text-muted-foreground"
                  )}
                  onClick={() => setVisibility('shared')}
                >
                  <Users className="h-3 w-3" />
                  Shared
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="pin-prompt"
              checked={isPinned}
              onCheckedChange={(checked) => setIsPinned(checked === true)}
            />
            <Label htmlFor="pin-prompt" className="flex items-center gap-1.5 cursor-pointer">
              <Star className="h-3.5 w-3.5" />
              Add to favorites
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={!title.trim() || !content.trim() || isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
